import { type RentMatrixPage, type RentRow, type RentRowGroup } from '@rentguru/commons-utils';
import { type RentMatrixVisibleRange } from '@up2rent/ui-new';
import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import last from 'lodash/last';
import range from 'lodash/range';
import sortedIndex from 'lodash/sortedIndex';
import sortedLastIndex from 'lodash/sortedLastIndex';
import uniq from 'lodash/uniq';

import { mergeRentInvalidation, type PendingRentInvalidation } from './rentLiveUpdateUtils';

export const RENT_MATRIX_PAGE_OVERSCAN = 50;
export const RENT_INVALIDATION_TRAILING_IN_MS = 1000;
export const RENT_INVALIDATION_MAX_WAIT_IN_MS = 5000;
export const FIRST_PAGE_INDEX = 0;
const MIN_PAGE_COUNT = 1;

export const getPageCount = (totalCount: number, pageSize: number): number =>
  Math.max(MIN_PAGE_COUNT, Math.ceil(totalCount / pageSize));

export const getPageIndices = (
  visibleRange: RentMatrixVisibleRange | null,
  pageSize: number,
  totalCount: number | null | undefined,
  overscan: number
): number[] => {
  if (isNil(visibleRange) || isNil(totalCount) || totalCount <= 0) {
    return [FIRST_PAGE_INDEX];
  }
  const lastPageIndex = getPageCount(totalCount, pageSize) - 1;
  const firstWindowPage = Math.min(
    lastPageIndex,
    Math.floor(Math.max(0, visibleRange.startIndex - overscan) / pageSize)
  );
  const lastWindowPage = Math.min(lastPageIndex, Math.floor((visibleRange.endIndex + overscan) / pageSize));
  return uniq([FIRST_PAGE_INDEX, ...range(firstWindowPage, lastWindowPage + 1)]);
};

export const clampPageIndices = (
  pageIndices: number[],
  totalCount: number | null | undefined,
  pageSize: number
): number[] => {
  if (isNil(totalCount)) {
    return [FIRST_PAGE_INDEX];
  }
  const pageCount = getPageCount(totalCount, pageSize);
  return uniq([FIRST_PAGE_INDEX, ...pageIndices.filter((pageIndex) => pageIndex < pageCount)]);
};

export const getSparseRow = <Row>(
  rowsByPage: ReadonlyMap<number, Row[]>,
  index: number,
  pageSize: number
): Row | undefined => rowsByPage.get(Math.floor(index / pageSize))?.[index % pageSize];

export interface RentMatrixGroupLayout {
  groups: RentRowGroup[];
  headerIndices: number[];
  rowCount: number;
}

export const buildGroupLayout = (groups: RentRowGroup[], totalCount: number): RentMatrixGroupLayout => ({
  groups,
  headerIndices: groups.reduce<number[]>((acc, _group, ordinal) => {
    const previousHeaderIndex = last(acc);
    acc.push(isNil(previousHeaderIndex) ? 0 : previousHeaderIndex + groups[ordinal - 1].count + 1);
    return acc;
  }, []),
  rowCount: totalCount + groups.length,
});

export const getGroupAt = (
  { groups, headerIndices }: RentMatrixGroupLayout,
  virtualIndex: number
): RentRowGroup | undefined => {
  const ordinal = sortedLastIndex(headerIndices, virtualIndex) - 1;
  return headerIndices[ordinal] === virtualIndex ? groups[ordinal] : undefined;
};

export const toDataIndex = ({ headerIndices }: RentMatrixGroupLayout, virtualIndex: number): number =>
  virtualIndex - sortedIndex(headerIndices, virtualIndex);

export const toDataRange = (
  layout: RentMatrixGroupLayout,
  { endIndex, startIndex }: RentMatrixVisibleRange
): RentMatrixVisibleRange => ({ endIndex: toDataIndex(layout, endIndex), startIndex: toDataIndex(layout, startIndex) });

export interface MappedPageRows<Row> {
  mapRow: (row: RentRow) => Row;
  rows: Row[];
}

export const mapRowsByPage = <Row>(
  pagesByIndex: ReadonlyMap<number, RentMatrixPage>,
  cache: WeakMap<RentMatrixPage, MappedPageRows<Row>>,
  mapRow: (row: RentRow) => Row
): Map<number, Row[]> =>
  new Map(
    [...pagesByIndex.entries()].map(([pageIndex, page]) => {
      const cached = cache.get(page);
      if (!isNil(cached) && cached.mapRow === mapRow) {
        return [pageIndex, cached.rows];
      }
      const rows = page.rows.map(mapRow);
      cache.set(page, { mapRow, rows });
      return [pageIndex, rows];
    })
  );

export const getLoadedRowIds = (pages: (RentMatrixPage | undefined)[]): Set<string> =>
  new Set(pages.flatMap((page) => (page?.rows ?? []).map((row) => row.rowId)));

export const indexRowsById = (pages: RentMatrixPage[]): Map<string, RentRow> =>
  new Map(pages.flatMap((page) => page.rows.map((row) => [row.rowId, row] as const)));

export const countLoadedRows = (pages: RentMatrixPage[]): number =>
  pages.reduce<number>((acc, page) => acc + page.rows.length, 0);

export interface RentPageState {
  data: RentMatrixPage | undefined;
  isError: boolean;
  isFetching: boolean;
  isPlaceholderData: boolean;
  pageIndex: number;
  refetch: () => void;
}

export interface RentMatrixPages {
  failedPageIndices: number[];
  isFetching: boolean;
  isPlaceholderData: boolean;
  pagesByIndex: ReadonlyMap<number, RentMatrixPage>;
  refetchFailed: () => void;
  settledPagesByIndex: ReadonlyMap<number, RentMatrixPage>;
}

export const summarizeRentPages = (pageStates: RentPageState[]): RentMatrixPages => {
  const failedPages = pageStates.reduce<RentPageState[]>(
    (acc, pageState) => (pageState.isError ? [...acc, pageState] : acc),
    []
  );
  const pagesByIndex = pageStates.reduce<Map<number, RentMatrixPage>>(
    (acc, { data, pageIndex }) => (isNil(data) ? acc : acc.set(pageIndex, data)),
    new Map()
  );
  const settledPagesByIndex = pageStates.reduce<Map<number, RentMatrixPage>>(
    (acc, { data, isPlaceholderData, pageIndex }) =>
      isNil(data) || isPlaceholderData ? acc : acc.set(pageIndex, data),
    new Map()
  );
  return {
    failedPageIndices: failedPages.map(({ pageIndex }) => pageIndex),
    isFetching: pageStates.some((pageState) => pageState.isFetching),
    isPlaceholderData: pageStates.some((pageState) => pageState.isPlaceholderData),
    pagesByIndex,
    refetchFailed: () => failedPages.forEach((pageState) => pageState.refetch()),
    settledPagesByIndex,
  };
};

interface InvalidationCoalescerOptions {
  maxWaitInMs: number;
  onFlush: (pending: PendingRentInvalidation) => void;
  trailingInMs: number;
}

export interface InvalidationCoalescer {
  cancel: () => void;
  push: (entityIds: string[] | null | undefined) => void;
}

export const createInvalidationCoalescer = ({
  maxWaitInMs,
  onFlush,
  trailingInMs,
}: InvalidationCoalescerOptions): InvalidationCoalescer => {
  let pending: PendingRentInvalidation | null = null;
  const flush = debounce(
    () => {
      const flushed = pending;
      pending = null;
      if (!isNil(flushed)) {
        onFlush(flushed);
      }
    },
    trailingInMs,
    { maxWait: maxWaitInMs }
  );
  return {
    cancel: () => {
      flush.cancel();
      pending = null;
    },
    push: (entityIds: string[] | null | undefined) => {
      pending = mergeRentInvalidation(pending, entityIds);
      flush();
    },
  };
};
